import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// ─── Thème PESACORE (identique au LandingScreen) ──────────────────────────────
const T = {
  bg:         "#0A0A0F",
  surface:    "#0f172a",
  surfaceAlt: "#0d1526",
  surfaceHov: "#162035",
  text:       "#f8fafc",
  textSub:    "#e2e8f0",
  textMuted:  "#c7c7c7",
  textFaint:  "#cecece",
  border:     "rgba(255,255,255,0.06)",
  borderMed:  "rgba(255,255,255,0.10)",
  orange:     "#fdba74",
  orangeDim:  "rgba(253,186,116,0.08)",
  sky:        "#7dd3fc",
  skyDim:     "rgba(125,211,252,0.08)",
  amber:      "#fcd34d",
  amberDim:   "rgba(252,211,77,0.08)",
  emerald:    "#6ee7b7",
  danger:     "#fca5a5",
};

const SECTIONS = [
  { id: "mentions",        label: "Mentions légales",         num: "01" },
  { id: "definitions",     label: "Définitions",              num: "02" },
  { id: "objet",           label: "Objet",                    num: "03" },
  { id: "acceptation",     label: "Acceptation",              num: "04" },
  { id: "description",     label: "Description du Service",   num: "05" },
  { id: "inscription",     label: "Inscription et comptes",   num: "06" },
  { id: "abonnements",     label: "Abonnements et tarifs",    num: "07" },
  { id: "essais",          label: "Essais & rétractation",    num: "08" },
  { id: "facturation",     label: "Facturation & paiement",   num: "09" },
  { id: "pi",              label: "Propriété intellectuelle", num: "10" },
  { id: "interdictions",   label: "Interdictions",            num: "11" },
  { id: "donnees",         label: "Données personnelles",     num: "12" },
  { id: "responsabilite",  label: "Limitation de responsabilité", num: "13" },
  { id: "resiliation",     label: "Résiliation & suspension", num: "14" },
  { id: "loi",             label: "Loi applicable",           num: "15" },
  { id: "modifications",   label: "Modifications des CGU",    num: "16" },
];

function Tag({ children, color = T.orange }) {
  return (
    <span style={{
      display: "inline-block",
      padding: "2px 10px",
      borderRadius: "3px",
      background: `${color}10`,
      border: `1px solid ${color}28`,
      color,
      fontSize: "10px",
      fontWeight: 700,
      fontFamily: "'JetBrains Mono', monospace",
      letterSpacing: "0.05em",
    }}>
      {children}
    </span>
  );
}

function SectionBlock({ id, num, title, children }) {
  return (
    <div
      id={id}
      style={{
        padding: "32px 0",
        borderBottom: `1px solid ${T.border}`,
        scrollMarginTop: "80px",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: "20px", marginBottom: "20px" }}>
        <span style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: "11px",
          fontWeight: 700,
          color: T.textFaint,
          minWidth: "28px",
          paddingTop: "4px",
        }}>
          {num}
        </span>
        <h2 style={{
          fontSize: "18px",
          fontWeight: 700,
          color: T.text,
          fontFamily: "'Inter', sans-serif",
          letterSpacing: "-0.01em",
          lineHeight: 1.3,
        }}>
          {title}
        </h2>
      </div>
      <div style={{ paddingLeft: "48px" }}>
        {children}
      </div>
    </div>
  );
}

function DataRow({ label, value, accent = false, color = T.orange }) {
  return (
    <div style={{
      display: "flex",
      gap: "16px",
      padding: "10px 14px",
      borderRadius: "4px",
      background: accent ? `${color}08` : "transparent",
      border: `1px solid ${accent ? `${color}20` : T.border}`,
      marginBottom: "6px",
      alignItems: "flex-start",
    }}>
      <span style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: "10px",
        fontWeight: 600,
        color: accent ? color : T.textFaint,
        minWidth: "180px",
        flexShrink: 0,
        paddingTop: "2px",
        letterSpacing: "0.02em",
      }}>
        {label}
      </span>
      <span style={{
        fontSize: "13px",
        color: T.textMuted,
        lineHeight: 1.65,
        fontFamily: "'Inter', sans-serif",
      }}>
        {value}
      </span>
    </div>
  );
}

function BulletList({ items, color = T.textMuted }) {
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "8px" }}>
      {items.map((item, i) => (
        <li key={i} style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
          <span style={{
            color: T.orange,
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: "10px",
            flexShrink: 0,
            paddingTop: "4px",
          }}>
            ▸
          </span>
          <span style={{ fontSize: "13px", color, lineHeight: 1.7, fontFamily: "'Inter', sans-serif" }}>
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

function AlertBox({ children, color = T.danger, icon = "⚠" }) {
  return (
    <div style={{
      padding: "12px 16px",
      borderRadius: "4px",
      background: `${color}08`,
      border: `1px solid ${color}22`,
      display: "flex",
      gap: "10px",
      alignItems: "flex-start",
      marginTop: "12px",
    }}>
      <span style={{ color, fontSize: "13px", flexShrink: 0 }}>{icon}</span>
      <span style={{ fontSize: "13px", color: T.textMuted, lineHeight: 1.65, fontFamily: "'Inter', sans-serif" }}>
        {children}
      </span>
    </div>
  );
}

// Plan pricing card
function PlanCard({ name, price, period, detail, color = T.orange, featured = false }) {
  return (
    <div style={{
      padding: "16px",
      borderRadius: "6px",
      background: featured ? `${color}06` : T.surfaceAlt,
      border: `1px solid ${featured ? `${color}25` : T.border}`,
      borderTop: `2px solid ${color}`,
    }}>
      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", fontWeight: 700, color, letterSpacing: "0.06em", marginBottom: "8px", textTransform: "uppercase" }}>
        {name}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: "6px", marginBottom: "6px" }}>
        <span style={{ fontSize: "22px", fontWeight: 800, color: T.text, fontFamily: "'Inter', sans-serif" }}>{price}</span>
        <span style={{ fontSize: "11px", color: T.textFaint }}>{period}</span>
      </div>
      {detail && (
        <div style={{ fontSize: "11px", color: T.textMuted, fontFamily: "'Inter', sans-serif", lineHeight: 1.55 }}>
          {detail}
        </div>
      )}
    </div>
  );
}

export default function CGU({ onBack }) {
  const [activeSection, setActiveSection] = useState("mentions");
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();
  const observer = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    observer.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveSection(e.target.id);
        });
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
    );
    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.current.observe(el);
    });
    return () => observer.current?.disconnect();
  }, [visible]);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: T.bg,
      fontFamily: "'Inter', 'JetBrains Mono', monospace",
      color: T.text,
      position: "relative",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:opsz,wght@14..32,300;14..32,400;14..32,500;14..32,600;14..32,700;14..32,800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes pulse { 0%,100%{opacity:.4} 50%{opacity:1} }
        .cg-nav-item { transition: all 0.15s; cursor: pointer; }
        .cg-nav-item:hover { color: #fdba74 !important; }
        .cg-placeholder {
          display: inline-block;
          background: rgba(253,186,116,0.08);
          border: 1px dashed rgba(253,186,116,0.35);
          border-radius: 3px;
          padding: 1px 8px;
          color: #fdba74;
          font-family: 'JetBrains Mono', monospace;
          font-size: 11px;
          letter-spacing: 0.03em;
        }
        .cg-def-term {
          font-family: 'JetBrains Mono', monospace;
          font-size: 11px;
          font-weight: 700;
          color: #fdba74;
          letter-spacing: 0.03em;
        }
      `}</style>

      {/* Grille fond */}
      <div style={{
        position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0,
        backgroundImage: `linear-gradient(to right,rgba(34,211,238,0.03) 1px,transparent 1px),linear-gradient(to bottom,rgba(245,158,11,0.03) 1px,transparent 1px)`,
        backgroundSize: "72px 72px",
        maskImage: "radial-gradient(ellipse at 50% 0%,black 30%,transparent 70%)",
      }} />

      {/* Header */}
      <header style={{
        height: "56px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 24px",
        borderBottom: `1px solid ${T.border}`,
        background: "rgba(10,10,15,0.92)",
        backdropFilter: "blur(12px)",
        position: "sticky",
        top: 0,
        zIndex: 100,
        fontFamily: "'JetBrains Mono', monospace",
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            background: "none", border: `1px solid ${T.border}`,
            cursor: "pointer", color: T.textFaint,
            padding: "5px 12px", borderRadius: "4px",
            fontSize: "11px", fontWeight: 500,
            transition: "all 0.2s", fontFamily: "'JetBrains Mono', monospace",
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = T.orange; e.currentTarget.style.color = T.orange; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textFaint; }}
        >
          ← retour
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <div style={{
            display: "flex", alignItems: "center", gap: "8px",
            padding: "4px 12px",
            background: T.surfaceAlt,
            borderRadius: "4px", border: `1px solid ${T.border}`,
          }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#10B981", animation: "pulse 2s infinite" }} />
            <span style={{ fontSize: "11px", fontWeight: 600, color: T.textFaint, letterSpacing: "0.05em" }}>
              PESACORE · LÉGAL
            </span>
          </div>
          <div style={{ width: 1, height: 28, background: T.border }} />
          <span style={{ fontSize: "11px", color: T.textFaint }}>v1.0</span>
        </div>

        <Tag>CGU/CGV · 18 MAI 2026</Tag>
      </header>

      {/* Layout : sidebar + contenu */}
      <div style={{
        maxWidth: "1100px",
        margin: "0 auto",
        padding: "0 24px",
        display: "flex",
        gap: "48px",
        position: "relative",
        zIndex: 5,
      }}>
        {/* Sidebar navigation */}
        <aside style={{
          width: "220px",
          flexShrink: 0,
          position: "sticky",
          top: "80px",
          alignSelf: "flex-start",
          paddingTop: "48px",
          paddingBottom: "48px",
          maxHeight: "calc(100vh - 100px)",
          overflowY: "auto",
        }}>
          <div style={{ marginBottom: "16px" }}>
            <span style={{
              fontSize: "10px", fontWeight: 700, color: T.textFaint,
              fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.07em",
            }}>
              SOMMAIRE
            </span>
          </div>
          <nav style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            {SECTIONS.map(({ id, label, num }) => {
              const isActive = activeSection === id;
              return (
                <div
                  key={id}
                  className="cg-nav-item"
                  onClick={() => scrollTo(id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "6px 10px",
                    borderRadius: "4px",
                    borderLeft: `2px solid ${isActive ? T.orange : "transparent"}`,
                    background: isActive ? T.orangeDim : "transparent",
                    color: isActive ? T.orange : T.textFaint,
                    fontSize: "11px",
                    fontFamily: "'JetBrains Mono', monospace",
                    transition: "all 0.15s",
                  }}
                >
                  <span style={{ fontSize: "9px", opacity: 0.6, flexShrink: 0 }}>{num}</span>
                  <span style={{ fontSize: "10px", lineHeight: 1.4 }}>{label}</span>
                </div>
              );
            })}
          </nav>

          <div style={{
            marginTop: "24px",
            padding: "12px 14px",
            borderRadius: "4px",
            background: T.surfaceAlt,
            border: `1px solid ${T.border}`,
          }}>
            <span style={{ fontSize: "10px", color: T.textFaint, fontFamily: "'JetBrains Mono', monospace", lineHeight: 1.7, display: "block" }}>
              ⓘ Droit français applicable.<br />
              Juridiction : <span className="cg-placeholder">MARSEILLE</span>
            </span>
          </div>
        </aside>

        {/* Contenu principal */}
        <main style={{
          flex: 1,
          paddingTop: "48px",
          paddingBottom: "80px",
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(16px)",
          transition: "opacity 0.55s ease, transform 0.55s ease",
        }}>
          {/* Hero */}
          <div style={{ marginBottom: "8px" }}>
            <div style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "10px",
              padding: "5px 14px",
              borderRadius: "4px",
              background: T.orangeDim,
              border: `1px solid ${T.orange}25`,
              marginBottom: "24px",
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              <span style={{ fontSize: "11px", fontWeight: 700, color: T.orange, letterSpacing: "0.08em" }}>
                ▸ CONDITIONS GÉNÉRALES D'UTILISATION ET DE VENTE
              </span>
              <span style={{ fontSize: "10px", color: T.orange, animation: "pulse 2s infinite" }}>●</span>
            </div>

            <h1 style={{
              fontSize: "clamp(28px, 3.5vw, 38px)",
              fontWeight: 700,
              color: T.text,
              lineHeight: 1.15,
              letterSpacing: "-0.025em",
              fontFamily: "'Inter', sans-serif",
              marginBottom: "16px",
            }}>
              CGU/CGV<br />
              <span style={{ color: T.orange }}>PESACORE Architect</span>
            </h1>

            <p style={{
              fontSize: "14px",
              color: T.textMuted,
              lineHeight: 1.75,
              maxWidth: "560px",
              fontStyle: "italic",
              fontFamily: "'Inter', sans-serif",
              marginBottom: "24px",
            }}>
              Les présentes conditions régissent l'accès et l'utilisation du Service PESACORE, ainsi que la vente des abonnements et prestations associées.
            </p>

            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <Tag>Version 1.0</Tag>
              <Tag color={T.sky}>Droit français</Tag>
              <Tag color={T.amber}>En vigueur au 09 avril 2026</Tag>
            </div>
          </div>

          <div style={{ height: "1px", background: T.border, margin: "32px 0" }} />

          {/* ── 01 Mentions légales ── */}
          <SectionBlock id="mentions" num="01" title="Mentions légales">
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <DataRow label="Éditeur du service" value="PESACORE Architect" accent />
              <DataRow label="Forme juridique / SIREN" value={<span className="cg-placeholder">SAS / 104 973 771</span>} />
              <DataRow label="Siège social" value={<span className="cg-placeholder">55 rue Grignan 13006 MARSEILLE</span>} />
              <DataRow label="N° Intracommunautaire" value={<span className="cg-placeholder">FR1303.104973771</span>} />
              <DataRow label="RCS" value={<span className="cg-placeholder">104 973 771 R.C.S. Marseille</span>} />
              <DataRow label="Contact" value={<span className="cg-placeholder">SuperAdmin : admin@pesacore.fr</span>} />
              <DataRow label="Hébergeur" value="LWS (Ligne Web Services) — serveurs en France" color={T.emerald} accent />
            </div>
          </SectionBlock>

          {/* ── 02 Définitions ── */}
          <SectionBlock id="definitions" num="02" title="Définitions">
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {[
                { term: "Service", def: "La plateforme PESACORE, accessible sur pesacore.fr — dénommée « Pesacore ». Système pédagogique offrant parcours structurés, simulations causales, traces décisionnelles, bifurcations, reconstitutions et transformations." },
                { term: "Contenu de la Plateforme", def: "Tous les templates, modèles opératoires, bibliothèque de modèles, ParcoursCreator, CaseCreator, AtelierCreator, CommmercialCreator, Centre d'Ajustement, ParcoursEngine, Atlas des Postures, Tableau Systémique des Elements, simulations de base, exemples pédagogiques, design, logiciels, interfaces et tout élément créé par l'Éditeur." },
                { term: "Contenu Utilisateur", def: "Les traces décisionnelles, bifurcations, dossiers, reconstitutions, simulations et tout élément créé ou importé par l'Utilisateur via les outils du Service." },
                { term: "Rôles", def: "Apprenant (particulier ou administré), Enseignant/Formateur/Recruteur, Administrateur organisationnel." },
                { term: "Dashboard Apprenant", def: "Interface dédiée permettant à l'Utilisateur Apprenant de suivre son parcours, ses traces, ses simulations et ses analytics décisionnels." },
                { term: "Dashboard Admin", def: "Interface de gestion (incluant fonctionnalités GrandeÉcole) permettant au rôle Administrateur de gérer les cohortes, utilisateurs administrés, accès et statistiques organisationnelles." },
              ].map(({ term, def }) => (
                <div key={term} style={{
                  padding: "12px 16px",
                  borderRadius: "4px",
                  background: T.surfaceAlt,
                  border: `1px solid ${T.border}`,
                  borderLeft: `3px solid ${T.orange}40`,
                }}>
                  <div className="cg-def-term" style={{ marginBottom: "6px" }}>{term}</div>
                  <div style={{ fontSize: "13px", color: T.textMuted, lineHeight: 1.65, fontFamily: "'Inter', sans-serif" }}>{def}</div>
                </div>
              ))}
            </div>
          </SectionBlock>

          {/* ── 03 Objet ── */}
          <SectionBlock id="objet" num="03" title="Objet">
            <p style={{ fontSize: "13px", color: T.textMuted, lineHeight: 1.75, fontFamily: "'Inter', sans-serif" }}>
              Les présentes CGU/CGV régissent l'accès et l'utilisation du Service PESACORE, ainsi que la vente des abonnements et prestations associées, que ce soit pour un usage{" "}
              <span style={{ color: T.text, fontWeight: 500 }}>individuel</span>,{" "}
              <span style={{ color: T.text, fontWeight: 500 }}>pédagogique</span>{" "}
              (collèges, lycées, universités, écoles) ou{" "}
              <span style={{ color: T.text, fontWeight: 500 }}>entreprise</span>.
            </p>
          </SectionBlock>

          {/* ── 04 Acceptation ── */}
          <SectionBlock id="acceptation" num="04" title="Acceptation des CGU/CGV">
            <AlertBox color={T.orange} icon="ⓘ">
              Toute inscription, création de compte ou utilisation du Service, y compris via les dashboards apprenant ou admin, vaut <strong style={{ color: T.orange }}>acceptation sans réserve</strong> des présentes CGU/CGV. Si vous n'acceptez pas ces conditions, vous ne devez ni accéder ni utiliser le Service.
            </AlertBox>
          </SectionBlock>

          {/* ── 05 Description du Service ── */}
          <SectionBlock id="description" num="05" title="Description du Service">
            <p style={{ fontSize: "13px", color: T.textMuted, lineHeight: 1.75, marginBottom: "16px", fontFamily: "'Inter', sans-serif" }}>
              PESACORE est un système numérique innovant permettant de <em>traverser</em> des situations réelles ou simulées (investigation, bifurcation, reconstitution, transformation) plutôt que de les lire simplement. Il repose sur une architecture propriétaire développée et éditée par <span style={{ color: T.text, fontWeight: 500 }}>PESACORE Architect</span>. 
              (I.H signifie Intelligence Humaine)
            </p>
            <BulletList items={[
              "Tableau systémique des éléments - outil guidé par I.H pour la lecture de la complexité systémique",
              "ParcoursCreator — outil guidé par I.H. pour la création de parcours structurés",
              "CaseCreator — outil guidé par I.H. pour la création de cas pédagogiques",
              "AtelierCreator — outil guidé par I.H. pour la création d'ateliers thématiques (audioconférence et visioconférence)",
              "CommercialCreator — outil guidé par I.H. pour la création de relations commerciales",
              "Simulations causales et exploration contrefactuelle",
              "Création et gestion de traces décisionnelles et portfolio de bifurcations",
              "Dashboard apprenant : suivi individuel, analytics décisionnels et débrief personnalisé",
              "Dashboard admin : gestion des cohortes, utilisateurs, accès et statistiques (fonctionnalités GrandeÉcole incluses)",
              "Exports des traces et rapports",
              "Gestion multi-cohortes et organisationnelle (rôle Administrateur)",
              "Options white-label, SSO, API et intégrations LMS selon le plan choisi",              
            ]} />
            
          </SectionBlock>

          {/* ── 06 Inscription ── */}
          <SectionBlock id="inscription" num="06" title="Inscription et comptes">
            <p style={{ fontSize: "13px", color: T.textMuted, lineHeight: 1.75, marginBottom: "12px", fontFamily: "'Inter', sans-serif" }}>
              L'inscription est réservée aux personnes physiques majeures et mineures à partir de 15 ans (autorisation parentale requise), ainsi qu'aux personnes morales. Un compte personnel est obligatoire pour accéder aux fonctionnalités du Service.
            </p>
            <AlertBox color={T.sky} icon="◎">
              Vous êtes entièrement responsable de la confidentialité de vos identifiants de connexion. Le rôle Administrateur permet la création et la gestion de comptes au sein d'une organisation.
            </AlertBox>
          </SectionBlock>

          {/* ── 07 Abonnements ── */}
          <SectionBlock id="abonnements" num="07" title="Abonnements et tarifs">
            <p style={{ fontSize: "11px", fontWeight: 700, color: T.textFaint, fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.05em", marginBottom: "12px" }}>
              PLANS INDIVIDUELS
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px", marginBottom: "20px" }}>
              <PlanCard name="Découverte" price="Gratuit" period="" detail="Accès limité — sans essai" color={T.textFaint} />
              <PlanCard name="Parcours" price="10 €" period="HT / mois" detail="Essai 30 jours inclus" color={T.sky} featured />
              <PlanCard name="Praticien" price="25 €" period="HT / mois" detail="Essai 30 jours inclus" color={T.orange} featured />
            </div>

            <p style={{ fontSize: "11px", fontWeight: 700, color: T.textFaint, fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.05em", marginBottom: "12px" }}>
              PLANS ORGANISATIONNELS
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px", marginBottom: "16px" }}>
              <PlanCard name="Academia / GrandeÉcole" price="2 400 €" period="HT / an" detail="Jusqu'à 150 apprenants" color={T.emerald} />
              <PlanCard name="Pro" price="9 600 €" period="HT / an" detail="Jusqu'à 500 collaborateurs" color={T.amber} featured />
              <PlanCard name="Entreprise" price="Sur devis" period="" detail="Illimité · SSO · SLA · White-label" color={T.danger} />
            </div>

            <p style={{ fontSize: "12px", color: T.textFaint, fontFamily: "'Inter', sans-serif", fontStyle: "italic" }}>
              Les tarifs peuvent être modifiés avec un préavis de 30 jours par email ou via le Service.
            </p>
          </SectionBlock>

          {/* ── 08 Essais ── */}
          <SectionBlock id="essais" num="08" title="Essais gratuits et droit de rétractation">
            <BulletList items={[
              "Tout nouvel abonné bénéficie d'un essai gratuit de 14 ou 30 jours selon le plan souscrit.",
              "Conformément à l'article L.221-18 du Code de la consommation, vous disposez d'un droit de rétractation de 14 jours à compter de la souscription.",
            ]} />
            <AlertBox color={T.amber} icon="⚠">
              Si vous activez l'accès aux fonctionnalités payantes pendant le délai de rétractation, vous acceptez expressément la perte du droit de rétractation pour la période consommée (art. L.221-28 C. conso.).
            </AlertBox>
          </SectionBlock>

          {/* ── 09 Facturation ── */}
          <SectionBlock id="facturation" num="09" title="Facturation et paiement">
            <BulletList items={[
              "Les abonnements sont souscrits pour une durée initiale puis renouvelés par tacite reconduction.",
              "Le paiement s'effectue par carte bancaire ou tout autre moyen proposé sur le Service.",
              "Résiliation possible à tout moment sur demande écrite auprès du back office, par courrier ou par courriel — effet à la fin de la période en cours.",
              "Tout retard de paiement entraîne la suspension du compte après mise en demeure.",
            ]} />
          </SectionBlock>

          {/* ── 10 Propriété intellectuelle ── */}
          <SectionBlock id="pi" num="10" title="Propriété intellectuelle – Droits d'auteur">
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {[
                {
                  sub: "10.1 — Propriété de l'Éditeur",
                  content: "Le Service dans son ensemble, l'ensemble des constructeurs, l'ensemble des templates, modèles opératoires, bibliothèque de modèles, simulations, interfaces (dashboards apprenant et admin) sont la propriété exclusive de PESACORE Architect, protégés par le droit d'auteur et l'ensemble des droits de propriété intellectuelle (art. L.111-1 et suiv. CPI).",
                  color: T.orange,
                },
                {
                  sub: "10.2 — Interdiction de reproduction",
                  content: "Toute reproduction, représentation, adaptation, modification, extraction, scraping, reverse-engineering, diffusion ou réutilisation (même partielle) du Contenu de la Plateforme est strictement interdite sans autorisation écrite préalable.",
                  color: T.danger,
                },
                {
                  sub: "10.3 — Contenu Utilisateur",
                  content: "Le Contenu Utilisateur reste la propriété de l'Utilisateur dans la limite de ses droits originaux. Tout Contenu Utilisateur créé à l'aide des outils de la Plateforme (CaseCreator, templates, modèles opératoires, simulations) constitue une œuvre dérivée du Contenu de la Plateforme.",
                  color: T.sky,
                },
                {
                  sub: "10.4 — Licence accordée à l'Éditeur",
                  content: "L'Utilisateur accorde à PESACORE Architect une licence mondiale, irrévocable, non exclusive, transférable et gratuite permettant d'héberger, sauvegarder, afficher, analyser, modifier, exploiter, reproduire, distribuer et utiliser ce Contenu aux fins de fourniture, amélioration et promotion du Service (y compris sous forme anonymisée).",
                  color: T.amber,
                },
                {
                  sub: "10.5 — Interdictions spécifiques",
                  content: "L'Utilisateur s'interdit d'extraire ou réutiliser hors de la Plateforme tout template, modèle opératoire ou structure pédagogique, ou d'utiliser le Contenu Utilisateur à des fins de concurrence ou de création d'un service similaire.",
                  color: T.danger,
                },
              ].map(({ sub, content, color }) => (
                <div key={sub} style={{
                  padding: "14px 16px",
                  borderRadius: "4px",
                  background: T.surfaceAlt,
                  border: `1px solid ${T.border}`,
                  borderLeft: `3px solid ${color}50`,
                }}>
                  <div style={{ fontSize: "11px", fontWeight: 700, color, fontFamily: "'JetBrains Mono', monospace", marginBottom: "8px", letterSpacing: "0.02em" }}>
                    {sub}
                  </div>
                  <div style={{ fontSize: "13px", color: T.textMuted, lineHeight: 1.7, fontFamily: "'Inter', sans-serif" }}>
                    {content}
                  </div>
                </div>
              ))}
            </div>
          </SectionBlock>

          {/* ── 11 Interdictions ── */}
          <SectionBlock id="interdictions" num="11" title="Interdictions et usages autorisés">
            <p style={{ fontSize: "13px", color: T.textMuted, lineHeight: 1.75, marginBottom: "16px", fontFamily: "'Inter', sans-serif" }}>
              Est strictement interdit tout usage contraire à l'ordre public, aux bonnes mœurs, ou visant à :
            </p>
            <BulletList color={T.danger} items={[
              "Effectuer du scraping ou de l'extraction massive de données ou de templates",
              "Réaliser du reverse-engineering du Service, de l'ensemble de la plateforme",
              "Reproduire ou diffuser hors plateforme tous les contenus accessibles, qu'il s'agisse des parcours, des simulations causales, des templates intégrés ou de tout autre type de contenu",
              "Créer des outils ou services concurrents à partir des modèles opératoires de PESACORE",
            ]} />
            <AlertBox color={T.danger} icon="⚠">
              Toute violation entraîne la suspension immédiate ou la résiliation du compte et pourra donner lieu à des poursuites pour contrefaçon.
            </AlertBox>
          </SectionBlock>

          {/* ── 12 Données personnelles ── */}
          <SectionBlock id="donnees" num="12" title="Données personnelles">
            <p style={{ fontSize: "13px", color: T.textMuted, lineHeight: 1.75, marginBottom: "12px", fontFamily: "'Inter', sans-serif" }}>
              L'Éditeur traite les données personnelles conformément à la{" "}
              <span style={{ color: T.sky }}>Politique de Confidentialité</span>{" "}
              disponible sur le site et au Règlement Général sur la Protection des Données (RGPD).
            </p>
            <AlertBox color={T.sky} icon="ⓘ">
              Pour les plans organisationnels (Academia, Pro, Entreprise), un accord de traitement des données (DPA) peut être signé sur demande.
            </AlertBox>
          </SectionBlock>

          {/* ── 13 Responsabilité ── */}
          <SectionBlock id="responsabilite" num="13" title="Limitation de responsabilité">
            <BulletList items={[
              "Le Service est fourni « en l'état » — l'Éditeur ne garantit ni l'absence d'erreur ni l'absence d'interruption.",
              "La responsabilité de l'Éditeur est limitée au montant des sommes effectivement perçues au cours des 12 derniers mois précédant le fait générateur.",
              "L'Éditeur ne saurait être tenu responsable des décisions prises par les Utilisateurs sur la base des simulations, traces ou dashboards générés par le Service.",
            ]} />
          </SectionBlock>

          {/* ── 14 Résiliation ── */}
          <SectionBlock id="resiliation" num="14" title="Résiliation – Suspension">
            <p style={{ fontSize: "13px", color: T.textMuted, lineHeight: 1.75, fontFamily: "'Inter', sans-serif" }}>
              L'Éditeur peut suspendre ou résilier un compte en cas de violation des présentes CGU/CGV, notamment en cas de non-paiement, d'usage illicite ou de violation des interdictions prévues à l'article 11.
            </p>
          </SectionBlock>

          {/* ── 15 Loi applicable ── */}
          <SectionBlock id="loi" num="15" title="Loi applicable et juridiction compétente">
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <DataRow label="Droit applicable" value="Droit français" accent color={T.orange} />
              <DataRow label="Juridiction compétente" value={<span className="cg-placeholder">MARSEILLE</span>} />
            </div>
          </SectionBlock>

          {/* ── 16 Modifications ── */}
          <SectionBlock id="modifications" num="16" title="Modifications des CGU/CGV">
            <p style={{ fontSize: "13px", color: T.textMuted, lineHeight: 1.75, fontFamily: "'Inter', sans-serif" }}>
              L'Éditeur se réserve le droit de modifier les présentes CGU/CGV. Toute modification sera notifiée par email ou via le Service.{" "}
              <span style={{ color: T.text, fontWeight: 500 }}>La poursuite de l'utilisation du Service après notification vaut acceptation des nouvelles conditions.</span>
            </p>
          </SectionBlock>

          {/* Footer du document */}
          <div style={{
            marginTop: "48px",
            padding: "20px 24px",
            borderRadius: "6px",
            background: T.surfaceAlt,
            border: `1px solid ${T.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "12px",
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            <span style={{ fontSize: "11px", color: T.textFaint }}>
              dernière mise à jour : 18 Mai 2026
            </span>
            <span style={{ fontSize: "11px", color: T.textFaint }}>
              PESACORE Architect — tous droits réservés
            </span>
          </div>
        </main>
      </div>
    </div>
  );
}